/**
 * Crop a source image (URL or data URL) to the supplied pixel region
 * and return a Blob suitable for upload.
 *
 * `area` matches `react-easy-crop`'s `croppedAreaPixels` shape:
 *   { x, y, width, height } — coordinates in the natural-size image space.
 *
 * Output is a square JPEG at quality 0.92. The cropper is configured
 * with aspect=1 so width === height in practice — we draw it on a
 * square canvas anyway as a defensive measure.
 */
export async function cropImageToBlob(src, area, outputSize = 768) {
  const img = await loadImage(src);
  const side = Math.max(1, Math.round(Math.min(area.width, area.height)));
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    area.x, area.y, side, side,
    0, 0, outputSize, outputSize,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob returned null"))),
      "image/jpeg",
      0.92,
    );
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // crossOrigin is required so the canvas isn't tainted when we
    // crop an image that's served from a different origin (our
    // /api/media proxy is same-origin in production, but the dev
    // preview routes through cloudflare).
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
